import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import API from "../../../util/api";
import ConfirmationModal from "../../Common/ConfirmationModal";
import { actionContainerStyles, actionLinkStyles, actionHeaderStyles } from "../common/ActionStyles";
import ResponsiveActions from "../common/ResponsiveActions";
import {
  adminTable,
  adminTableDataCell,
} from "../common/AdminTableStyles";
import { getSubscriptionDetail } from "../../../sagas/actions";

const LINK_HOVER_COLOR = "#F7941D";

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5em;
  width: 100%;

  @media (max-width: ${breakpoints.tablet}px) {
    gap: 1.25em;
  }

  @media (max-width: ${breakpoints.phone}px) {
    flex-direction: column;
    gap: 1em;
  }
`;

const Actions = styled.div`
  ${actionContainerStyles};
  // border: 1px solid ${({ colors }) => colors.border};

  h3 {
    ${actionHeaderStyles};
    border-bottom: 1px solid ${({ colors }) => colors.border};
  }

  ul {
    margin: 0;
    padding: 12px 16px;
    list-style: none;
  }

  li + li {
    margin-top: 4px;
  }

  a {
    ${actionLinkStyles};
    padding-left: 16px;
    color: #2C4960;

    &:hover {
      color: ${LINK_HOVER_COLOR};
    }
  }
  @media (min-width: ${breakpoints.tablet}px) {
    flex: 0 0 260px;
  }
`;

const ViewSection = styled.div`
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 2px 5px 5px rgba(0, 0, 0, 0.05);
`;

const Title = styled.h1`
  color: ${({ colors }) => colors.labelText};
  margin: 10px;
  font-size: 1.75em;
  font-weight: normal;

  @media (max-width: ${breakpoints.phone}px) {
    font-size: 1.25em;
    text-align: center;
  }
`;

const DataTable = styled.table`
  ${adminTable};
  margin-top: 1em;

  thead {
    display: none;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 0.5em 0;
    border-bottom: none;
  }

  tbody tr:nth-child(odd) {
    background-color: #f5f5f5;
  }

  tbody tr:nth-child(even) {
    background-color: #ffffff;
  }

  td:first-child {
    font-weight: bold;
    width: 150px;
  }
`;

const Form = styled.form`
  fieldset {
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 4px;
    margin: 0 0 1em;
    padding: 1em;
  }
  legend {
    padding: 0 0.5em;
    font-weight: bold;
    color: ${({ colors }) => colors.labelText};
  }
  .input {
    margin-bottom: 1em;
    width: 90%;
  }
  .input label {
    display: block;
    margin-bottom: 0.3em;
    font-weight: bold;
    color: ${({ colors }) => colors.labelText};
  }
  .input input {
    width: 100%;
    padding: 0.5em;
    font-size: 1em;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 4px;
  }
  .submit {
    text-align: right;
  }
  .submit input {
    padding: 0.6em 1.2em;
    font-size: 1em;
    border: none;
    border-radius: 4px;
    background: ${({ colors }) => colors.buttonBackground};
    color: white;
    cursor: pointer;
    &:hover {
      background: ${({ colors }) => colors.buttonHoverBackground};
    }
  }
`;

export default function POCContent({ colors }) {
  const selected = useSelector((s) => s.auth.selected_subscriptions) || {};
  const dispatch = useDispatch();

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    name: selected.contact_name || "",
    email: selected.contact_email || "",
    phone: selected.contact_phone || "",
    extension: selected.contact_extension || "",
  });
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState({ show: false, title: "", message: "", status: "Success" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);
    API.updatePOC({
      invoice_id: selected.invoice_id,
      change_poc_name:      form.name,
      change_poc_email:     form.email,
      change_poc_phone:     form.phone,
      change_poc_extension: form.extension,
    })
      .then((resp) => {
        setModal({
          show: true,
          title: resp?.status === "Failed" ? "Error" : "Success",
          message: resp.message || "Point of contact updated.",
          status: resp?.status === "Failed" ? "Failed" : "Success",
        });
        if (resp?.status !== "Failed") {
          dispatch(getSubscriptionDetail(
            selected.invoice_id,
            selected.customer_id,
            selected.customer_code
          ));
        }
        setEditMode(false);
      })
      .catch((err) => {
        setModal({
          show: true,
          title: "Error",
          message: err.message || "Failed to update point of contact.",
          status: "Failed",
        });
      })
      .finally(() => setSaving(false));
  };

  const closeModal = () => {
    setModal((m) => ({ ...m, show: false, status: "Success" }));
  };

  const formattedPhone = (num) =>
    num ? num.replace(/(\d{3})(\d{3})(\d{4})/, "($1)$2-$3") : "";

  return (
    <Container>
      <ResponsiveActions
        colors={colors}
        mobileToggleTop={-15}
        mobileToggleLeft={-8}
        renderActions={() => (
          <Actions colors={colors}>
            <h3>Actions</h3>
            <ul>
              {!editMode ? (
                <li>
                  <a onClick={() => setEditMode(true)}>Edit</a>
                </li>
              ) : (
                <li>
                  <a onClick={() => setEditMode(false)}>Cancel</a>
                </li>
              )}
            </ul>
          </Actions>
        )}
      />

      <ViewSection>
        <Title colors={colors}>Point of Contact</Title>

        {!editMode ? (
          <DataTable>
            <tbody>
              <tr className="altrow">
                <td>Name</td>
                <td>{selected.contact_name}&nbsp;</td>
              </tr>
              <tr>
                <td>Email</td>
                <td>{selected.contact_email}&nbsp;</td>
              </tr>
              <tr className="altrow">
                <td>Phone</td>
                <td>
                  {formattedPhone(selected.contact_phone)}
                  {selected.contact_extension
                    ? ` Ext ${selected.contact_extension}`
                    : ""}
                  &nbsp;
                </td>
              </tr>
            </tbody>
          </DataTable>
        ) : (
          <Form colors={colors} onSubmit={handleSave}>
            <fieldset>
              <legend>Point of Contact</legend>
              <div className="input">
                <label htmlFor="poc-name">Name</label>
                <input
                  id="poc-name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="input">
                <label htmlFor="poc-email">Email</label>
                <input
                  id="poc-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="input">
                <label htmlFor="poc-phone">Phone</label>
                <input
                  id="poc-phone"
                  name="phone"
                  type="text"
                  value={form.phone}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="input">
                <label htmlFor="poc-ext">Extension</label>
                <input
                  id="poc-ext"
                  name="extension"
                  type="text"
                  value={form.extension}
                  onChange={handleChange}
                />
              </div>
            </fieldset>
            <div className="submit">
              <input
                type="submit"
                value={saving ? "Saving..." : "Save"}
                disabled={saving}
              />
            </div>
          </Form>
        )}
      </ViewSection>

      <ConfirmationModal
        show={modal.show}
        title={modal.title}
        message={modal.message}
        onCancel={closeModal}
        onConfirm={closeModal}
        status={modal.status}
        single
      />
    </Container>
  );
}
